// ===================================================================
// DISASTER GRANTS — Program Selector
// Landing page for choosing between PA and HMGP grant programs
// ===================================================================

import { useNavigate } from 'react-router-dom';
import { Title, Text, Paper, Group, Stack, SimpleGrid, ThemeIcon, Button } from '../../components/gab/GabComponents';
import {
  IconBuildingBank, IconShieldCheck, IconArrowRight, IconFileInvoice, IconHome,
} from '@tabler/icons-react';
import { useModulePermission } from '../../hooks/useModulePermission';

export default function ProgramSelector() {
  const navigate = useNavigate();
  const { canView } = useModulePermission('pagrants');

  if (!canView) {
    return (
      <div style={{ padding: 24 }}>
        <Paper p="xl" radius="md" ta="center" style={{ background: '#f1f3f5' }}>
          <Text size="lg" c="dimmed">You do not have access to Disaster Grants.</Text>
        </Paper>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 900, margin: '0 auto' }}>
      <Stack gap="xl">
        <Stack gap={4} ta="center">
          <Title order={2}>Disaster Grants</Title>
          <Text size="sm" c="dimmed">Select a grant program to manage</Text>
        </Stack>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
          {/* Public Assistance */}
          <Paper
            withBorder radius="md" p="xl"
            style={{ cursor: 'pointer', transition: 'box-shadow 150ms ease, transform 150ms ease', borderTop: '4px solid #1E40AF' }}
            onClick={() => navigate('/pagrants/pa')}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}
          >
            <Stack gap="md">
              <Group gap="sm">
                <ThemeIcon size={48} radius="md" color="#1E40AF" variant="light">
                  <IconBuildingBank size={28} />
                </ThemeIcon>
                <div>
                  <Text size="lg" fw={700}>Public Assistance</Text>
                  <Text size="xs" c="dimmed">FEMA PA Program (Section 406)</Text>
                </div>
              </Group>

              <Text size="sm" c="dimmed">
                Restore disaster-damaged facilities and infrastructure. Manage project worksheets,
                cost documentation, reimbursements, time extensions, and FEMA compliance for
                Categories A through G.
              </Text>

              <Stack gap={4}>
                <Group gap={6}><IconFileInvoice size={14} color="var(--color-secondary-700)" /><Text size="xs" c="dimmed">Project Worksheets & Cost Tracking</Text></Group>
                <Group gap={6}><IconHome size={14} color="var(--color-secondary-700)" /><Text size="xs" c="dimmed">Damage Categories A-G</Text></Group>
                <Group gap={6}><IconShieldCheck size={14} color="var(--color-secondary-700)" /><Text size="xs" c="dimmed">406 Hazard Mitigation Proposals</Text></Group>
              </Stack>

              <Button
                variant="light" color="#1E40AF" rightSection={<IconArrowRight size={16} />}
                fullWidth mt="xs"
              >
                Open PA Grants
              </Button>
            </Stack>
          </Paper>

          {/* HMGP */}
          <Paper
            withBorder radius="md" p="xl"
            style={{ cursor: 'pointer', transition: 'box-shadow 150ms ease, transform 150ms ease', borderTop: '4px solid #059669' }}
            onClick={() => navigate('/pagrants/hmgp')}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}
          >
            <Stack gap="md">
              <Group gap="sm">
                <ThemeIcon size={48} radius="md" color="var(--color-success)" variant="light">
                  <IconShieldCheck size={28} />
                </ThemeIcon>
                <div>
                  <Text size="lg" fw={700}>Hazard Mitigation</Text>
                  <Text size="xs" c="dimmed">FEMA HMGP (Section 404)</Text>
                </div>
              </Group>

              <Text size="sm" c="dimmed">
                Reduce future disaster risk through mitigation projects. Manage subapplications,
                benefit-cost analyses, state prioritization, and FEMA award tracking for
                acquisition, elevation, retrofit, and infrastructure hardening.
              </Text>

              <Stack gap={4}>
                <Group gap={6}><IconFileInvoice size={14} color="var(--color-secondary-700)" /><Text size="xs" c="dimmed">Subapplications & BCA</Text></Group>
                <Group gap={6}><IconHome size={14} color="var(--color-secondary-700)" /><Text size="xs" c="dimmed">Acquisition, Elevation, Retrofit, Safe Rooms</Text></Group>
                <Group gap={6}><IconShieldCheck size={14} color="var(--color-secondary-700)" /><Text size="xs" c="dimmed">State Priority & FEMA Award Tracking</Text></Group>
              </Stack>

              <Button
                variant="light" color="var(--color-success)" rightSection={<IconArrowRight size={16} />}
                fullWidth mt="xs"
              >
                Open HMGP
              </Button>
            </Stack>
          </Paper>
        </SimpleGrid>
      </Stack>
    </div>
  );
}
